'use client';

import { HStack, VStack } from '@terra/ui-styled-system';
import { Badge, BodyStrong, MetaDefault } from '@terra/ui-design-system';
import { formatDuration } from '@terra/ui-formatters';
import { useTranslations } from 'next-intl';
import type { AttackPathEventNodeData } from './attack-path-event-node.types';
import { InternalNetworkGraphPopoverCard } from './InternalNetworkGraphPopoverCard';

type InternalNetworkAttackPathEventPopoverProps = {
  data: AttackPathEventNodeData;
};

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <HStack justifyContent="space-between" gap={3} w="full" alignItems="baseline">
    <MetaDefault color="fg.subtle" flexShrink={0}>
      {label}
    </MetaDefault>
    <MetaDefault color="fg.default" textAlign="right" truncate minW={0}>
      {value}
    </MetaDefault>
  </HStack>
);

export function InternalNetworkAttackPathEventPopover({
  data,
}: InternalNetworkAttackPathEventPopoverProps) {
  const tGraph = useTranslations('internalNetwork.attackPathGraph');
  const tKinds = useTranslations('internalNetwork.attackPathEventKinds');
  const tEntityKinds = useTranslations(
    'internalNetwork.crownJewels.entityKinds',
  );
  const isFailed = data.status === 'FAILED';
  const kindLabel =
    data.role === 'ENTITY' && data.entityKind
      ? tEntityKinds(data.entityKind)
      : tKinds(data.kind);
  const rows = [
    data.hostname
      ? { label: tGraph('detail.host'), value: data.hostname }
      : null,
    data.subtitle
      ? { label: tGraph('detail.path'), value: data.subtitle }
      : null,
    data.caption
      ? { label: tGraph('detail.context'), value: data.caption }
      : null,
    data.severity
      ? { label: tGraph('detail.severity'), value: data.severity }
      : null,
    data.mitreTechniqueId
      ? { label: tGraph('detail.technique'), value: data.mitreTechniqueId }
      : null,
    data.role === 'EVENT' && data.elapsedSeconds > 0
      ? {
          label: tGraph('detail.elapsed'),
          value: formatDuration(data.elapsedSeconds * 1000),
        }
      : null,
  ].filter((row): row is { label: string; value: string } => row !== null);

  return (
    <InternalNetworkGraphPopoverCard
      onClick={(event) => event.stopPropagation()}
      onContextMenu={(event) => event.stopPropagation()}
    >
      <VStack gap={2} alignItems="stretch" px={3} py={3}>
        <HStack justifyContent="space-between" gap={2} alignItems="center">
          <MetaDefault color="fg.muted" truncate minW={0}>
            {kindLabel}
          </MetaDefault>
          {isFailed ? (
            <Badge variant="outline" size="sm" colorPalette="red">
              {tGraph('eventFailed')}
            </Badge>
          ) : data.isProven ? (
            <Badge variant="outline" size="sm" colorPalette="green">
              {tGraph('eventProven')}
            </Badge>
          ) : null}
        </HStack>

        <BodyStrong color="fg.default">{data.title}</BodyStrong>

        {rows.length > 0 ? (
          <VStack gap={1} alignItems="stretch">
            {rows.map((row) => (
              <DetailRow key={row.label} label={row.label} value={row.value} />
            ))}
          </VStack>
        ) : null}
      </VStack>
    </InternalNetworkGraphPopoverCard>
  );
}
